import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { createHttpContext } from '@app/core/http/http-context';
import { ApiUrlService } from '@app/shared/services/api-url.service';
import { dayjs } from '@app/shared/dayjs-setup';
import { __ } from '@app/shared/functions/object.functions';
import { ApplicationUser } from '@app/shared/models/classes/ApplicationUser';
import { IdentityToken } from '@app/shared/models/classes/IdentityToken';
import { Role } from '@app/shared/models/classes/Role';
import { Ticket } from '@app/shared/models/classes/Ticket';
import { UsersService } from '@app/shared/services/users.service';
import { environment } from '@env/environment';
import { Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, mergeMap } from 'rxjs/operators';

const credentialsKey = '349haisadiugz82973082hdasjdi8';
const identityRequestContext = () =>
  createHttpContext({ skipAccessToken: true, skipApiPrefix: true, skipErrorHandler: true });

@Service()
export class AuthenticationService {
  private httpClient = inject(HttpClient);
  private usersService = inject(UsersService);
  private apiUrlService = inject(ApiUrlService);

  readonly clientId = 'siudfho2349!ihasd8gi3$ihsdo';
  readonly clientSecret = 'as9dzho2=gu8aishd8!89h3o';

  private _credentials!: IdentityToken | null;
  private _credentials$: Subject<IdentityToken> = new Subject<IdentityToken>();

  credentials$: Observable<IdentityToken> = this._credentials$.asObservable();

  constructor() {
    const savedCredentials = localStorage.getItem(credentialsKey);
    if (!__.IsNullOrUndefined(savedCredentials)) {
      try {
        this._credentials = JSON.parse(savedCredentials);
      } catch {
        localStorage.removeItem(credentialsKey);
      }
    }
  }

  login(body: AccessTokenRequestBody, _remember?: boolean): Observable<ApplicationUser> {
    const headers = new HttpHeaders().set('Content-Type', `application/x-www-form-urlencoded`);

    return this.httpClient
      .post<Ticket>(
        `${this.apiUrlService.getApiUrl()}connect/token`,
        `grant_type=password&username=${encodeURIComponent(body.email)}&password=${encodeURIComponent(body.password)}`,
        { headers, context: identityRequestContext() },
      )
      .pipe(
        mergeMap((result: Ticket) => {
          const identityToken = {
            access_token: result.access_token,
            expires: dayjs().add(result.expires_in, 'second').toISOString(),
            refresh_token: result.refresh_token,
            user: null as unknown as ApplicationUser,
          };

          this.setCredentials(identityToken, true);

          return this.usersService.getCurrentUser().pipe(
            map((user: ApplicationUser) => {
              identityToken.user = user;
              this.setCredentials(identityToken, true);
              return user;
            }),
          );
        }),
      );
  }

  get user(): ApplicationUser {
    return this._credentials!.user;
  }

  get ticket(): { accessToken: string; expires: string; refreshToken: string } {
    return {
      accessToken: this._credentials!.access_token,
      expires: this._credentials!.expires,
      refreshToken: this._credentials!.refresh_token,
    };
  }

  hasRole(group: string) {
    if (__.IsNullOrUndefinedOrEmpty(group)) {
      return true;
    }
    if (environment.testPermissions === false && environment.production !== true) {
      return true;
    }
    const roles = this.credentials?.user?.roles;
    if (!__.IsNullOrUndefined(roles)) {
      if (this.isCompanyAdministrator()) {
        return true;
      }
      return roles.some((q) => q === group);
    }
    return false;
  }

  hasAnyRole(groups: string[]) {
    if (groups.length === 0) {
      return true;
    }
    if (environment.testPermissions === false && environment.production !== true) {
      return true;
    }
    const roles = this.credentials?.user?.roles;
    if (!__.IsNullOrUndefined(roles)) {
      if (this.isCompanyAdministrator()) {
        return true;
      }
      return groups.findIndex((a) => roles.some((q) => q === a)) > -1;
    }
    return false;
  }

  isCompanyAdministrator(): boolean {
    return (this.credentials?.user?.roles ?? []).indexOf(Role.CompanyAdministrator) > -1;
  }

  isAdministrator(): boolean {
    return (this.credentials?.user?.roles ?? []).indexOf(Role.Administrator) > -1;
  }

  isAnyAdministrator(): boolean {
    return this.isAdministrator() || this.isCompanyAdministrator();
  }

  refreshToken(): Observable<Ticket> {
    const headers = new HttpHeaders().set('Content-Type', `application/x-www-form-urlencoded`);

    return this.httpClient
      .post<Ticket>(
        `${this.apiUrlService.getApiUrl()}connect/token`,
        `grant_type=refresh_token&refresh_token=${this.credentials!.refresh_token}`,
        { headers, context: identityRequestContext() },
      )
      .pipe(
        map((result: Ticket) => {
          const identityToken = {
            access_token: result.access_token,
            expires: dayjs().add(result.expires_in, 'second').toISOString(),
            refresh_token: this.ticket.refreshToken,
            user: this._credentials!.user,
          };

          this.setCredentials(identityToken, true);
          return result;
        }),
      );
  }

  logout(): Observable<boolean> {
    return this.httpClient.get(`${this.apiUrlService.getApiUrl()}users/logout`).pipe(
      catchError((_error: unknown) => of(false)),
      finalize(() => this.setCredentials()),
      map((success: unknown) => success as boolean),
    );
  }

  isAuthenticated(): boolean {
    return !__.IsNullOrUndefined(this._credentials) && !__.IsNullOrUndefined(this._credentials.user);
  }

  get credentials(): IdentityToken | null {
    return this._credentials;
  }

  setCredentials(credentials?: IdentityToken, _remember?: boolean) {
    this._credentials = credentials || null;
    this._credentials$.next(this._credentials!);

    if (credentials) {
      localStorage.setItem(credentialsKey, JSON.stringify(credentials));
    } else {
      localStorage.removeItem(credentialsKey);
    }
  }
}

export class AccessTokenRequestBody {
  email!: string;
  password!: string;
}

export class AccessTokenResponse {
  access_token!: string;
  expires!: string;
  refresh_token!: string;
}
